import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Newspaper, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { NewsIntelligenceRow } from '@/hooks/useNewsIntelligence';
import { ArticleDetailModal } from '@/components/news/ArticleDetailModal';

export function RecentNewsHighlight() {
  const [selectedArticle, setSelectedArticle] = useState<NewsIntelligenceRow | null>(null);

  const { data: articles, isLoading } = useQuery({
    queryKey: ['dashboard-recent-news'],
    queryFn: async (): Promise<NewsIntelligenceRow[]> => {
      const { data, error } = await supabase
        .from('news_intelligence' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      return (data as unknown as NewsIntelligenceRow[]) || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const signalColor = (type: string | null) => {
    switch (type) {
      case 'funding': return 'bg-green-100 text-green-700 border-green-200';
      case 'partnership': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'clinical_trial': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'regulatory': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const signalLabel = (type: string | null) => {
    if (!type) return null;
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const priorityDot = (level: number | null) => {
    if (level === 1) return 'bg-red-500';
    if (level === 2) return 'bg-yellow-500';
    return 'bg-muted-foreground/30';
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Recent News</CardTitle>
            </div>
            <Link to="/news">
              <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : !articles?.length ? (
            <p className="text-sm text-muted-foreground text-center py-6">No news articles yet</p>
          ) : (
            <div className="space-y-2">
              {articles.map((article) => (
                <div
                  key={article.id}
                  className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedArticle(article)}
                >
                  <div className="flex items-center gap-2 shrink-0 mt-1">
                    <div className={`h-2 w-2 rounded-full ${priorityDot(article.priority_level)}`} />
                    {article.signal_type && (
                      <Badge variant="outline" className={`text-[10px] ${signalColor(article.signal_type)}`}>
                        {signalLabel(article.signal_type)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight line-clamp-2">{article.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {article.source_feed}
                      {article.published_at && (
                        <> · {formatDistanceToNow(new Date(article.published_at), { addSuffix: true })}</>
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="pt-3 text-center">
            <Link to="/news" className="text-xs text-primary hover:underline font-medium">
              View All News →
            </Link>
          </div>
        </CardContent>
      </Card>

      <ArticleDetailModal
        article={selectedArticle}
        open={!!selectedArticle}
        onOpenChange={(open) => { if (!open) setSelectedArticle(null); }}
      />
    </>
  );
}
